import { useCallback, useState } from 'react'
import type { MatterChatHistoryNavItem } from '@/chat-common/conversation_list/hooks/useMatterChatHistory'

export type UseChatHistoryActions = {
  /** id of the row whose overflow menu is currently open, if any */
  menuOpenForId: string | null
  setMenuOpenForId: (id: string | null) => void
  renameChatItem: MatterChatHistoryNavItem | null
  renameChatModalOpen: boolean
  openRenameChatModal: (item: MatterChatHistoryNavItem) => void
  closeRenameChatModal: () => void
  deleteChatItem: MatterChatHistoryNavItem | null
  deleteChatModalOpen: boolean
  openDeleteChatModal: (item: MatterChatHistoryNavItem) => void
  closeDeleteChatModal: () => void
}

/**
 * Rename/delete dialog state for the side nav's Chat History list, plus which row's overflow
 * menu is open — one dialog pair shared across all rows, keyed to whichever item's menu action
 * opened it. "Open" is just "an item is set" — there's no case where a dialog is open without an
 * item or has an item without being open, so a separate open/closed flag would only be able to
 * drift out of sync with the item, not add information.
 */
export function useChatHistoryActions(): UseChatHistoryActions {
  const [menuOpenForId, setMenuOpenForId] = useState<string | null>(null)
  const [renameChatItem, setRenameChatItem] = useState<MatterChatHistoryNavItem | null>(null)
  const [deleteChatItem, setDeleteChatItem] = useState<MatterChatHistoryNavItem | null>(null)

  const openRenameChatModal = useCallback((item: MatterChatHistoryNavItem) => {
    setRenameChatItem(item)
  }, [])

  const closeRenameChatModal = useCallback(() => {
    setRenameChatItem(null)
  }, [])

  const openDeleteChatModal = useCallback((item: MatterChatHistoryNavItem) => {
    setDeleteChatItem(item)
  }, [])

  const closeDeleteChatModal = useCallback(() => {
    setDeleteChatItem(null)
  }, [])

  return {
    menuOpenForId,
    setMenuOpenForId,
    renameChatItem,
    renameChatModalOpen: renameChatItem !== null,
    openRenameChatModal,
    closeRenameChatModal,
    deleteChatItem,
    deleteChatModalOpen: deleteChatItem !== null,
    openDeleteChatModal,
    closeDeleteChatModal,
  }
}
